import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// ============================================================================
// DATABASE TYPES
// ============================================================================

export type GenderType = 'male' | 'female' | 'non_binary' | 'other'
export type RelationshipType = 'casual' | 'serious' | 'friendship' | 'networking'
export type MessageType = 'text' | 'image' | 'gif' | 'emoji' | 'voice' | 'video'
export type TransactionStatus = 'pending' | 'completed' | 'failed' | 'cancelled' | 'refunded'
export type ReportStatus = 'pending' | 'reviewed' | 'resolved' | 'dismissed'

export interface Profile {
  id: string
  user_id: string
  
  // Basic Information
  pi_username: string
  first_name: string
  last_name: string
  email: string
  phone: string
  date_of_birth?: string
  age: number
  gender: GenderType
  
  // Profile Content
  bio: string
  occupation: string
  education: string
  interests: string[]
  languages: string[]
  photos: string[]
  
  // Location
  location_city: string
  location_country: string
  latitude: number
  longitude: number
  
  // Preferences
  looking_for: GenderType[]
  relationship_type: RelationshipType
  min_age: number
  max_age: number
  max_distance: number
  
  // Status & Settings
  is_active: boolean
  is_premium: boolean
  is_verified: boolean
  is_pi_pioneer: boolean
  show_age: boolean
  show_distance: boolean
  balance: number
  
  // Privacy Settings
  profile_visibility: string
  allow_messages_from: string
  
  // Timestamps
  last_active: string
  created_at: string
  updated_at: string
}

export interface Like {
  id: string
  from_user_id: string
  to_user_id: string
  is_super: boolean
  message: string
  created_at: string
}

export interface Match {
  id: string
  user1_id: string
  user2_id: string
  created_at: string
  is_active: boolean
}

export interface Conversation {
  id: string
  user1_id: string
  user2_id: string
  created_at: string
  last_message_at: string
  is_active: boolean
}

export interface Message {
  id: string
  conversation_id: string
  sender_id: string
  receiver_id: string
  content: string
  message_type: MessageType
  media_url: string
  created_at: string
  is_read: boolean
  read_at?: string
  is_deleted: boolean
}

export interface Block {
  id: string
  blocker_id: string
  blocked_id: string
  reason: string
  created_at: string
}

export interface Report {
  id: string
  reporter_id: string
  reported_id: string
  reason: string
  description: string
  status: ReportStatus
  created_at: string
  reviewed_at?: string
  reviewed_by?: string
}

export interface Transaction {
  id: string
  user_id: string

  // Payment Method Information
  payment_method: string
  payment_method_last4: string
  pi_payment_id: string
  pi_transaction_id: string
  stripe_payment_id: string
  stripe_charge_id: string
  external_transaction_id: string

  // Transaction Details
  type: 'credit' | 'debit'
  amount: number
  currency: string
  product_type: string
  product_details: any
  description: string
  receipt_url: string

  // Status & Reasons
  status: TransactionStatus
  refund_reason: string
  failed_reason: string

  // Timestamps
  created_at: string
  completed_at?: string
  refunded_at?: string
  failed_at?: string
  cancelled_at?: string
  updated_at: string

  // Security & Tracking
  ip_address: string
  user_agent: string
  metadata: any
}

export interface UserSettings {
  id: string
  user_id: string
  
  // Notification Settings
  push_notifications: boolean
  email_notifications: boolean
  new_matches: boolean
  new_messages: boolean
  super_likes: boolean
  profile_views: boolean
  promotions: boolean
  
  // Privacy Settings
  show_online_status: boolean
  show_read_receipts: boolean
  
  // App Settings
  theme: string
  language: string
  
  created_at: string
  updated_at: string
}

export interface ProfileView {
  id: string
  viewer_id: string
  viewed_id: string
  created_at: string
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const getProfileImageUrl = (profile: Profile): string => {
  if (profile.photos && profile.photos.length > 0) {
    return profile.photos[0]
  }
  return `https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=400`
}

export const getFullName = (profile: Profile): string => {
  return `${profile.first_name} ${profile.last_name}`.trim()
}

export const calculateAge = (dateOfBirth: string): number => {
  const today = new Date()
  const birthDate = new Date(dateOfBirth)
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }
  
  return age
}

export const formatDistance = (distanceKm: number): string => {
  if (distanceKm < 1) {
    return 'Less than 1 km away'
  } else if (distanceKm < 10) {
    return `${Math.round(distanceKm)} km away`
  } else {
    return `${Math.round(distanceKm)} km away`
  }
}

export const formatLastSeen = (lastActive: string): string => {
  const now = new Date()
  const lastSeenDate = new Date(lastActive)
  const diffInMinutes = Math.floor((now.getTime() - lastSeenDate.getTime()) / (1000 * 60))
  
  if (diffInMinutes < 1) {
    return 'Active now'
  } else if (diffInMinutes < 60) {
    return `Active ${diffInMinutes} minutes ago`
  } else if (diffInMinutes < 1440) {
    const hours = Math.floor(diffInMinutes / 60)
    return `Active ${hours} hour${hours > 1 ? 's' : ''} ago`
  } else {
    const days = Math.floor(diffInMinutes / 1440)
    return `Active ${days} day${days > 1 ? 's' : ''} ago`
  }
}

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

export const validateProfile = (profile: Partial<Profile>): string[] => {
  const errors: string[] = []
  
  if (!profile.first_name || profile.first_name.trim().length < 2) {
    errors.push('First name must be at least 2 characters')
  }
  
  if (!profile.last_name || profile.last_name.trim().length < 2) {
    errors.push('Last name must be at least 2 characters')
  }
  
  if (!profile.email || !/\S+@\S+\.\S+/.test(profile.email)) {
    errors.push('Valid email is required')
  }
  
  if (!profile.age || profile.age < 18 || profile.age > 100) {
    errors.push('Age must be between 18 and 100')
  }
  
  if (profile.bio && profile.bio.length > 500) {
    errors.push('Bio must be less than 500 characters')
  }
  
  if (profile.min_age && profile.max_age && profile.min_age > profile.max_age) {
    errors.push('Minimum age cannot be greater than maximum age')
  }
  
  return errors
}

export const validateMessage = (content: string): string[] => {
  const errors: string[] = []
  
  if (!content || content.trim().length === 0) {
    errors.push('Message cannot be empty')
  }
  
  if (content.length > 1000) {
    errors.push('Message must be less than 1000 characters')
  }
  
  return errors
}